import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { ExportIcon } from "components/SVGs";
import { writeFile } from "xlsx";
import { FireBaseContext } from "contexts/FireBase";
import { Typography } from "@mui/material";
import { useAppSelector } from "app/hooks";
import {
  ReportType,
  selectBreadCrumbLabels,
  selectFormativeData,
  selectReportType,
} from "features/Reports";

const customStyles = {
  root: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },
  disable: {
    pointerEvents: "none",
    cursor: "default",
  },
};

function ExportReport() {
  const breadCrumbLabels = useAppSelector(selectBreadCrumbLabels);
  const reportType = useAppSelector(selectReportType);
  const data = useAppSelector(selectFormativeData);
  const { logEvent } = useContext(FireBaseContext);
  const { t } = useTranslation();

  const sheetName = `${reportType} report`;

  const getEntity = (reportType: string) => {
    switch (reportType) {
      case ReportType.Teacher_Report:
      case ReportType.Grade_Report:
        return breadCrumbLabels[1];
      case ReportType.Class_Report:
        return breadCrumbLabels[2];
      case ReportType.Teachers_Report:
      case ReportType.Grades_Report:
      default:
        return reportType;
    }
  };
  const mainColumns = [
    { t: "s", v: t("Reading") },
    { t: "s", v: t("Writing") },
    { t: "s", v: t("Listening") },
    { t: "s", v: t("Language") },
  ];
  const columns = [
    { t: "s", v: t("assignmentCompletionAvg") },
    { t: "s", v: t("evaluationCompletionAvg") },
    { t: "s", v: t("studentsAvg") },
  ];
  const columnsData =
    data?.length &&
    data.reduce((result: any, item: any) => {
      result[item.domainLanguageCode] = {
        assignmentCompletionAvg: {
          t: "s",
          v: item.totalAssignedStudents
            ? Math.round(
                (item.avgCompletedStudents / item.totalAssignedStudents) * 100
              ) + "%"
            : "-",
        },
        evaluationCompletionAvg: {
          t: "s",
          v: item.submittedAssessments
            ? Math.round(
                (item.evaluatedAssessments / item.submittedAssessments) * 100
              ) + "%"
            : "-",
        },
        studentsAvg: {
          t: "s",
          v: Math.round(item.avgScore) ? Math.round(item.avgScore) + "%" : "-",
        },
      };

      return result;
    }, {});

  const exportHandler = (reportType: ReportType) => {
    logEvent("ClickOnExportExcel", { Source: reportType });
    const today = new Date();
    const exportDate =
      today.getFullYear() + "-" + today.getMonth() + "-" + today.getDate();
    writeFile(
      {
        SheetNames: [t(sheetName)],
        Sheets: {
          [t(sheetName)]: {
            "!ref": "A1:M9",
            "!cols": [
              { wch: 25 },
              { wch: 22 },
              { wch: 22 },
              { wch: 22 },
              { wch: 22 },
              { wch: 22 },
              { wch: 22 },
              { wch: 22 },
              { wch: 22 },
              { wch: 22 },
              { wch: 22 },
              { wch: 22 },
              { wch: 22 },
              { wch: 22 },
            ],
            A1: { t: "s", v: "" },
            A3: {
              t: "s",
              v: t(getEntity(reportType)),
            },
            B1: mainColumns[0],
            B2: columns[0],
            C2: columns[1],
            D2: columns[2],
            B3: columnsData["Reading"].assignmentCompletionAvg,
            C3: columnsData["Reading"].evaluationCompletionAvg,
            D3: columnsData["Reading"].studentsAvg,
            E1: mainColumns[1],
            E2: columns[0],
            F2: columns[1],
            G2: columns[2],
            E3: columnsData["Writing"].assignmentCompletionAvg,
            F3: columnsData["Writing"].evaluationCompletionAvg,
            G3: columnsData["Writing"].studentsAvg,
            H1: mainColumns[2],
            H2: columns[0],
            I2: columns[1],
            J2: columns[2],
            H3: columnsData["Listening"].assignmentCompletionAvg,
            I3: columnsData["Listening"].evaluationCompletionAvg,
            J3: columnsData["Listening"].studentsAvg,
            K1: mainColumns[3],
            K2: columns[0],
            L2: columns[1],
            M2: columns[2],

            K3: columnsData["Language"]?.assignmentCompletionAvg,
            L3: "-",
            M3: columnsData["Language"]?.studentsAvg,
            "!merges": [
              { s: { r: 0, c: 1 }, e: { r: 0, c: 3 } } /* B1:D1 */,
              { s: { r: 0, c: 4 }, e: { r: 0, c: 6 } } /* E1:G1 */,
              { s: { r: 0, c: 7 }, e: { r: 0, c: 9 } } /* H1:J1 */,
              { s: { r: 0, c: 10 }, e: { r: 0, c: 12 } } /* K1:M1 */,
            ],
          },
        },
      },
      `${t(sheetName)}${
        reportType !== "teachers" && reportType !== "grades"
          ? "-" + t(getEntity(reportType))
          : ""
      }-${exportDate}.xlsx`
    );
  };

  return (
    <Typography
      sx={{
        ...customStyles.root,
        ...(!data?.length ? customStyles.disable : {}),
      }}
      onClick={reportType ? () => exportHandler(reportType) : undefined}
    >
      <ExportIcon
        sx={{
          color:
            !data || !data.length
              ? "rgb(77, 191, 241, 0.3)"
              : "rgb(77, 191, 241)",
        }}
      />
    </Typography>
  );
}

export default ExportReport;
